import { numberWithCommas } from '@openhouse-technologies/utils'
import React from 'react'

import CoinIcon from 'assets/images/coin.png'

import * as Styles from '../styles'

/**
 * The full line-by-line breakup of `excel_payment_summary`.
 *
 * Rendered generically rather than by picking known rows out of the array (which is what
 * BookWithRupees does): the backend already sends this as an ordered, labelled ledger —
 * `sequence`, `label`, `sub_label`, `operation` — so anything it adds later (a new kit
 * line, an admission fee, a second discount) shows up here without a frontend change. The
 * hand-picked approach silently drops rows it was not written to know about, which is how
 * the e-mandate screens ended up showing a total with no explanation of how it was reached.
 *
 * Two rows get special treatment:
 *   - `operation: "subtraction"` renders red with a minus, so a discount or a coin burn
 *     reads as money coming off rather than another charge.
 *   - "Coins Used" is denominated in coins, not rupees, so it carries the coin icon.
 */

const isPayableNow = (item) =>
  item?.type === 'Total' && `${item?.label}`.toLowerCase() === 'payable now'

const PaymentBreakup = ({ summary, heading = 'payment breakup' }) => {
  const rows = (summary || [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))

  if (!rows.length) {
    return null
  }

  const renderValue = (item) => {
    const subtraction = item?.operation === 'subtraction'
    const value = numberWithCommas(Math.abs(item?.value ?? 0))

    if (item?.type === 'Coins Used') {
      return (
        <Styles.CoinsContainer>
          <Styles.CoinImg src={CoinIcon} alt="Coin" />
          <div style={{ color: 'red' }}>-{value}</div>
        </Styles.CoinsContainer>
      )
    }

    return (
      <span style={subtraction ? { color: 'red' } : undefined}>
        {subtraction ? '-' : ''}₹{value}
      </span>
    )
  }

  const renderLabel = (item) => (
    <Styles.SummaryLabel>
      <span>{item?.label}</span>
      {item?.sub_label ? (
        <Styles.SummarySubLabel>{item.sub_label}</Styles.SummarySubLabel>
      ) : null}
    </Styles.SummaryLabel>
  )

  return (
    <>
      <Styles.Subheading>{heading}</Styles.Subheading>
      <Styles.PaymentSummary>
        {rows.map((item, index) => {
          const Row = isPayableNow(item)
            ? Styles.TotalPayable
            : Styles.SummaryItem
          return (
            <Row
              key={`${item?.type}-${item?.label}-${item?.sequence ?? index}`}
            >
              {renderLabel(item)}
              {renderValue(item)}
            </Row>
          )
        })}
      </Styles.PaymentSummary>
    </>
  )
}

export default PaymentBreakup
